import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, Link as LinkIcon, FileCheck } from "lucide-react";
import { Link } from "react-router-dom";
import StarBorder from "../../components/signO/ui/StarBorder";

const BlockchainPage = () => {
  return (
    <div className="min-h-screen bg-dark text-white p-8 pt-24">
      <Link
        to="/"
        className="fixed top-8 left-8 text-gray-400 hover:text-white transition-colors z-50"
      >
        <ArrowLeft className="w-6 h-6" />
      </Link>

      <div className="max-w-4xl mx-auto space-y-16">
        {/* Hero Section */}
        <section className="text-center space-y-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600"
          >
            Signature Security Anchor: The Power of Blockchain Immutability
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-400 max-w-2xl mx-auto"
          >
            We go beyond simple encryption. Every document signed on our
            platform is digitally fingerprinted, hashed, and anchored to an
            immutable public ledger.
          </motion.p>
        </section>

        {/* Feature 1 */}
        <section className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-4">
            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
              <Shield className="w-6 h-6" />
            </div>
            <h2 className="text-3xl font-bold">
              Generating the Digital Fingerprint
            </h2>
            <p className="text-gray-400 text-lg">
              When a document is finalized, our system generates a unique,
              irreversible cryptographic hash—the document's true digital
              identity.
            </p>
          </div>
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            {/* Placeholder for visual/diagram */}
            <div className="h-48 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center">
              <span className="font-mono text-sm text-blue-300">
                0x7f83b1657ff1fc53b92dc18148a1d65dfc2d4b1fa3d677284addd200126d9069
              </span>
            </div>
          </div>
        </section>

        {/* Feature 2 */}
        <section className="grid md:grid-cols-2 gap-12 items-center md:flex-row-reverse">
          <div className="order-2 md:order-1 bg-white/5 rounded-2xl p-8 border border-white/10">
            <div className="h-48 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center">
              <LinkIcon className="w-16 h-16 text-purple-400 opacity-50" />
            </div>
          </div>
          <div className="space-y-4 order-1 md:order-2">
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400">
              <LinkIcon className="w-6 h-6" />
            </div>
            <h2 className="text-3xl font-bold">The Immutability Layer</h2>
            <p className="text-gray-400 text-lg">
              We mint this cryptographic hash onto a dedicated blockchain
              network, permanently timestamping the document and making the
              record decentralized and unchangeable.
            </p>
          </div>
        </section>

        {/* Feature 3 */}
        <section className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-4">
            <h2 className="text-3xl font-bold">Tamper-Proof Audit Trails</h2>
            <p className="text-gray-400 text-lg">
              The blockchain records every interaction: document creation,
              views, and final signature application.
            </p>
          </div>

          <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-xl p-6 text-center">
            <p className="text-blue-300 font-medium mb-2">Check the Hash</p>
            <p className="text-gray-400">
              Verify your document's history on the public Blockchain Explorer.
            </p>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center pt-8">
          <Link to="/signup">
            <StarBorder
              as="button"
              color="cyan"
              speed="4s"
              className="text-lg px-8 py-4"
            >
              Start Securing My Documents
            </StarBorder>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlockchainPage;
